import { Markdown } from "@app/lib/markdown.tsx";
import type { CkbfsRef, DocEntry } from "@manifest/schema.ts";
import { useEffect, useState } from "react";
import { CellMetaHeader } from "./CellMetaHeader.tsx";

interface Props {
  doc: DocEntry;
  prev?: DocEntry | undefined;
  next?: DocEntry | undefined;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  fetchBytes: (ref: CkbfsRef) => Promise<Uint8Array>;
}

export function ReaderModal({ doc, prev, next, onClose, onPrev, onNext, fetchBytes }: Props) {
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setText(null);
    setError(null);
    fetchBytes(doc.ckbfs).then(
      (bytes) => {
        if (cancelled) return;
        setText(new TextDecoder().decode(bytes));
      },
      (e) => {
        if (cancelled) return;
        setError((e as Error).message);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [doc.ckbfs, fetchBytes]);

  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && onPrev) onPrev();
      if (e.key === "ArrowRight" && onNext) onNext();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, onPrev, onNext]);

  return (
    <article aria-labelledby="reader-title" className="text-parchment-900">
      <div className="max-w-3xl mx-auto px-6 py-12 max-w-full">
        <div className="flex items-center justify-between mb-8">
          <button
            type="button"
            onClick={onClose}
            className="text-parchment-700 hover:text-parchment-900 text-sm font-mono transition-colors"
            aria-label="Close reader"
          >
            ← back to library
          </button>
          <div className="flex gap-2 text-xs font-mono text-parchment-700">
            {prev && onPrev && (
              <button
                type="button"
                onClick={onPrev}
                className="px-2 py-1 hover:bg-parchment-200/60 hover:text-parchment-900 rounded transition-colors"
              >
                ← {prev.title.slice(0, 30)}
              </button>
            )}
            {next && onNext && (
              <button
                type="button"
                onClick={onNext}
                className="px-2 py-1 hover:bg-parchment-200/60 hover:text-parchment-900 rounded transition-colors"
              >
                {next.title.slice(0, 30)} →
              </button>
            )}
          </div>
        </div>

        <h1 id="reader-title" className="text-3xl font-serif mb-2 text-parchment-900">
          {doc.title}
        </h1>
        <CellMetaHeader doc={doc} />

        {text === null && !error && (
          <div className="text-parchment-600 italic">fetching from chain…</div>
        )}
        {error && (
          <div className="text-red-800 bg-red-50 border border-red-300 p-4 rounded">
            <div className="font-bold mb-1">Couldn't reach chain</div>
            <div className="text-sm">{error}</div>
            <button
              type="button"
              onClick={() => {
                setError(null);
                fetchBytes(doc.ckbfs).then(
                  (b) => setText(new TextDecoder().decode(b)),
                  (e) => setError((e as Error).message),
                );
              }}
              className="mt-3 text-sm underline hover:text-red-900"
            >
              Retry
            </button>
          </div>
        )}
        {text !== null && (
          <Markdown {...(doc.images ? { imageRefs: doc.images } : {})} fetchBytes={fetchBytes}>
            {text}
          </Markdown>
        )}
      </div>
    </article>
  );
}
